import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { Bell, BellOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function PushNotificationPrompt() {
  const { isSupported, isSubscribed, permission, subscribe } = usePushNotifications();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Detect iOS standalone (PWA installed) vs browser
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const needsInstall = isIOS && !isStandalone;

  useEffect(() => {
    const d = sessionStorage.getItem('push-prompt-dismissed');
    if (d) setDismissed(true);
  }, []);

  if (isSubscribed || permission === 'denied' || dismissed) return null;
  // On non-iOS, check support normally. On iOS, show install hint if not installed.
  if (!isSupported && !needsInstall) return null;

  const handleEnable = async () => {
    if (needsInstall) {
      toast.info('To get notifications on iPhone, tap the Share button (⬆) and "Add to Home Screen" first, then enable notifications from the installed app.', { duration: 8000 });
      return;
    }
    setLoading(true);
    const ok = await subscribe();
    setLoading(false);
    if (ok) {
      toast.success('Push notifications enabled!');
    } else {
      toast.error('Could not enable notifications. Please check your browser settings.');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('push-prompt-dismissed', 'true');
  };

  return (
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 max-w-sm animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="push-notification-prompt"
    >
      <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-500/20">
            <Bell className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">Enable Notifications</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {needsInstall
                ? 'Add this app to your Home Screen first, then enable notifications'
                : 'Get instant alerts for orders, quotations, and factory requests'}
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleEnable} disabled={loading} data-testid="enable-push-btn">
                <Bell className="h-3.5 w-3.5 mr-1.5" />
                {needsInstall ? 'How to Install' : loading ? 'Enabling...' : 'Enable'}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss} data-testid="dismiss-push-btn">
                Not now
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function PushToggle() {
  const { isSupported, isSubscribed, permission, subscribe, unsubscribe } = usePushNotifications();
  const [loading, setLoading] = useState(false);

  if (!isSupported) return null;

  const handleToggle = async () => {
    setLoading(true);
    if (isSubscribed) {
      await unsubscribe();
      toast.success('Push notifications disabled');
    } else {
      const ok = await subscribe();
      if (ok) toast.success('Push notifications enabled!');
      else if (permission === 'denied') toast.error('Notifications blocked. Enable in browser settings.');
      else toast.error('Could not enable notifications');
    }
    setLoading(false);
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading || permission === 'denied'}
      className={cn(
        "flex items-center justify-center h-8 w-8 md:h-9 md:w-9 rounded-md border transition-colors",
        isSubscribed
          ? "text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10"
          : "text-slate-500 dark:text-slate-400 border-slate-200 dark:border-neutral-800 hover:bg-slate-50 dark:hover:bg-neutral-900",
        (loading || permission === 'denied') && "opacity-50 cursor-not-allowed"
      )}
      data-testid="push-toggle-btn"
      title={isSubscribed ? 'Notifications On' : 'Notifications Off'}
    >
      {isSubscribed ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
    </button>
  );
}
